import { classnames } from 'Utils/classnames/classnames.utils';
import React, { Fragment, useContext } from 'react';
import {
  filterActivities,
  flatActivities,
  flatFacilities,
  getActivitiesCategoryIcon,
  sortCategoryOnDemandActivitiesByStartingPrice,
} from 'Components/utils/activities/activities.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { ServicesContext } from 'Context/ServicesContext';
import { t } from 'Src/i18n';
import { upSellTypeMapping } from 'Views/booking/booking-confirmation/BookingConfirmationTracking.utils';
import { Badge } from 'Components/frameworks/badge/Badge';
import { Hr } from 'Components/frameworks/hr/Hr';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Slider } from 'Components/frameworks/slider/Slider';
import { Tabs } from 'Components/frameworks/tabs/Tabs';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import { ActivitiesFilteredList } from 'Components/widgets/activities-filtered-list/ActivitiesFilteredList';
import { ActivitiesList } from 'Components/widgets/activities-list/ActivitiesList';
import { ActivityCard } from 'Components/widgets/activity-card/ActivityCard';
import { AvailabilityBadge } from 'Components/widgets/availability-badge/AvailabilityBadge';
import { Hero } from 'Components/widgets/hero/Hero';
import { ImmersiveSlideShow } from 'Components/widgets/immersive-slideshow/ImmersiveSlideShow';
import { InformationCard } from 'Components/widgets/information-card/InformationCard';
import { Facilities } from './Facilities';

const getCols = (isDesktop, isTablet) => {
  if (isDesktop) {
    return 3;
  }

  if (isTablet) {
    return 2;
  }

  return 1;
};

type Props = {
  category: ActivitiesCategoryEntity | ActivitiesThematicEntity;
};
export const LayerActivitiesBody = ({ category }: Props) => {
  const { isDesktop, isTablet } = useResponsive();
  const { bookingId, hasRentals } = useContext(ServicesContext);

  if (!category) {
    return null;
  }

  const cols = getCols(isDesktop, isTablet);
  const {
    activities,
    bookingDescription,
    description,
    equipmentRentalDescription,
    id,
    images,
    keywords,
    name,
    shortDescription,
    supervisionDescription,
    termsAndConditionsDescription,
  } = category || {};
  const flattenActivities = flatActivities([], category);
  const includedActivities = flattenActivities.filter(filterActivities(true));
  const onDemandActivities = (activities || []).filter(filterActivities(false));
  const flattenFacilities = flatFacilities([], category);
  const outsideFacilities = flattenFacilities.filter((facility) => !facility.isOnSite);
  const resortFacilities = flattenFacilities.filter((facility) => facility.isOnSite);
  const information = [
    {
      text: bookingDescription,
      title: t('common.bookingLabel'),
    },
    {
      text: supervisionDescription,
      title: t('common.supervisionLabel'),
    },
    {
      text: termsAndConditionsDescription,
      title: t('common.termsAndConditionsLabel'),
    },
    {
      text: equipmentRentalDescription,
      title: t('common.equipments'),
    },
  ].filter(({ text }) => Boolean(text));
  const catName = name || t('booking.activities.otherActivities');
  const message =
    id === 'AC_Wellness'
      ? {
          title: t('booking.activities.wellness.priceAndAvailibiltyInformations'),
          subtitle: t('booking.activities.wellness.priceAndAvailibiltyInformations2'),
        }
      : {};
  const items = (category.categories || [])
    .map((c) => {
      const categoryOnDemandActivities = flatActivities([], c).filter(filterActivities(false));
      const onlineCategoryOnDemandActivitiesSorted = sortCategoryOnDemandActivitiesByStartingPrice(
        categoryOnDemandActivities,
      );

      if (!onlineCategoryOnDemandActivitiesSorted.length) {
        return null;
      }

      return {
        header: {
          title: c.name,
        },
        headerId: c.id,
        panelId: `panel-${c.id}`,
        content: (
          <ActivitiesFilteredList
            category={{ ...c, activities: onlineCategoryOnDemandActivitiesSorted }}
            colsNumber={cols}
            hasRentals={hasRentals}
            message={message}
          />
        ),
      };
    })
    .filter(Boolean);
  return (
    <div
      className={classnames('flex flex-col', {
        'pb-80': !information.length,
      })}
    >
      <div className="relative mb-70 md:mb-60">
        {images?.length > 1 ? (
          <ImmersiveSlideShow
            controlsTheme="activities"
            isPaginationDisabled={false}
            immersiveSize="medium"
            media={images}
            nextLabel="next"
            pictureAltLabel="picture"
            previousLabel="previous"
          />
        ) : (
          <Hero className="bg-darkGrey" size="medium" url={images?.[0]} />
        )}
        <div className="absolute bottom-0 w-full translate-y-1/2 transform text-center leading-0">
          <Badge
            className="size-140 border-white bg-white text-black md:size-120"
            iconName={getActivitiesCategoryIcon(keywords, id)}
            iconSize="3.25rem"
            variant="pill"
          />
        </div>
      </div>
      <SiteContainer>
        <div className="flex flex-col gap-20 md:flex-row">
          <div className="md:basis-2/5">
            <Title dataTestId="Activities-Layer-Header" theme="title-brand-n44">
              {catName}
            </Title>
            {shortDescription && <p className="text-20 md:text-24">{shortDescription}</p>}
          </div>
          {description && (
            <div className="md:basis-3/5">
              <Text
                data-testid="Activities-Layer-Description"
                text={description}
                theme="text-sans-n3"
              />
            </div>
          )}
        </div>
      </SiteContainer>

      {!!includedActivities.length && (
        <SiteContainer className="py-40">
          <p className="mb-20 flex items-center gap-5 text-14 font-bold leading-1 text-green">
            <AvailabilityBadge />
            {t('common.included')}
          </p>
          <Slider
            cols={cols > 1 ? cols : 0}
            hasControls={isTablet}
            navPosition="top"
            nbItemsToScroll={cols}
            nextLabel={t('common.next')}
            previousLabel={t('common.previous')}
            scrollAreaClassName="overflow-hidden -mx-10"
            scrollAreaContainerClassName="overflow-auto scroll-x scrollbar-hidden snap-x snap-mandatory"
            scrollItemClassName={classnames('w-full max-w-full flex-none overflow-hidden p-10', {
              'w-[calc(100vw-2*1.25rem)]': !(cols > 1) && includedActivities.length > 1,
            })}
          >
            {includedActivities.map((a) => (
              <ActivityCard activity={a} bookingId={bookingId} key={a.id} />
            ))}
          </Slider>
        </SiteContainer>
      )}

      {(!!onDemandActivities.length ||
        (hasRentals && category.upsellType === upSellTypeMapping.WINTER_SPORT) ||
        !!category.categories?.length) && (
        <SiteContainer className="py-40">
          {!!category.categories?.length && (
            <Tabs
              isContentUnmountedOnCollapse
              isScrollable
              items={items}
              id="activities"
              scrollBackwardLabel={t('common.scrollBackward')}
              scrollForwardLabel={t('common.scrollForward')}
            />
          )}
          {(!!onDemandActivities.length ||
            (hasRentals && category.upsellType === upSellTypeMapping.WINTER_SPORT)) && (
            <Fragment>
              <p className="mb-20 flex items-center gap-5 text-14 font-bold text-orange">
                <AvailabilityBadge isOnDemand />
                {t('common.onDemand')}
              </p>
              <ActivitiesList
                activities={sortCategoryOnDemandActivitiesByStartingPrice(onDemandActivities)}
                colsNumber={cols}
                category={category}
                hasRentals={hasRentals}
              />
            </Fragment>
          )}
        </SiteContainer>
      )}

      {(!!resortFacilities.length || !!outsideFacilities.length) && (
        <SiteContainer>
          <Title className="border-b-1 border-lightGrey" theme="title-brand-n1">
            {t('common.activities.facilitiesLabel')}
          </Title>
          <Facilities
            className="py-40"
            items={resortFacilities}
            title={t('booking.activities.resortFacilities')}
          />
          <Facilities
            className="py-40"
            items={outsideFacilities}
            title={t('booking.activities.outsideFacilities')}
          />
        </SiteContainer>
      )}

      {!!information.length && (
        <Fragment>
          <Hr />
          <SiteContainer className="mb-100 mt-auto py-40">
            <section className="gap-20 md:columns-2">
              {information.map(({ text, title }) => (
                <InformationCard
                  className="mb-40 break-inside-avoid-column"
                  key={title}
                  text={text}
                  title={title}
                />
              ))}
            </section>
          </SiteContainer>
        </Fragment>
      )}
    </div>
  );
};
